import React, { Fragment, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { Card, CardBody, Col, Row, Table, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Alert, Button } from "reactstrap";
import { useDispatch } from "react-redux";
import { addPartnerInvoiceData } from "../store/partnerSlice";
import DropZone from "../../../share-components/upload/dropzone";
import { toast } from "react-toastify";
import moment from "moment";
import { DatePicker } from "rsuite";

const mainTableColumn = [
  "mp_order_number",
  "ean_number",
  "order_date",
  "payout_date",
  "title",
  "sku",
  "transaction_date",
  "commision_gross_amount",
  "commision_net_amount",
  "commision_net_vat",
  "country",
  "currency",
  "quantity",
  "amount",
  "payment_type",
  "total_amount_gross",
  "total_amount_net",
  "total_amount_vat",
  "transaction_type",
];

function UploadInvoices({
  invoiceColumns = {},
  isHavingColumns,
  setIsEditMode,
  isEditMode,
  setHasChanges,
  hasChanges,
  setfields,
  setIsHavingColumns,
  partner,
  setMappingFile,
  mappingFile
}) {
  const dispatch = useDispatch();
  const [showWarning, setShowWarning] = useState(true);
  const [uploadedHeaders, setUploadedHeaders] = useState([]);
  const [mapping, setMapping] = useState(invoiceColumns || {});
  const [headers, setHeaders] = useState([]);
  const [selectedHeader, setSelectedHeader] = useState("");
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(mainTableColumn.reduce((acc, col) => ({ ...acc, [col]: false }), {}));
  const [manualPayout, setManualPayout] = useState(false);
  const [payoutDate, setPayoutDate] = useState(null);

  const [selectedHeaders, setSelectedHeaders] = useState(() =>
    mainTableColumn.reduce((acc, col) => {
      acc[col] = invoiceColumns && typeof invoiceColumns === "object" ? invoiceColumns[col] ?? null : null;
      return acc;
    }, {})
  );

  useEffect(() => {
    if (invoiceColumns && Object.keys(invoiceColumns).length) {
      setSelectedHeaders((prev) => ({
        ...prev,
        ...mainTableColumn.reduce((acc, col) => {
          acc[col] = invoiceColumns[col] ?? prev[col];
          return acc;
        }, {}),
      }));
    }
  }, [JSON.stringify(invoiceColumns)]);

  useEffect(() => {
    if (!invoiceColumns || !invoiceColumns.payout_date) return;

    const value = invoiceColumns.payout_date;

    if (moment(value, "YYYY-MM-DD", true).isValid()) {
      setManualPayout(true); 
      setPayoutDate(moment(value, "YYYY-MM-DD").toDate()); 
      setSelectedHeaders((prev) => ({
        ...prev,
        payout_date: null,
      }));
    }

    else {
      setManualPayout(false); 
      setPayoutDate(null);
      setSelectedHeaders((prev) => ({
        ...prev,
        payout_date: value,
      }));
    }
  }, [invoiceColumns]);

  const toggleDropdown = (col) => {
    setDropdownOpen({ ...dropdownOpen, [col]: !dropdownOpen[col] });
  };

  const handleSelect = (col, header) => {
    setSelectedHeaders((prev) => {
      const newHeaders = { ...prev, [col]: header };

      const changed = mainTableColumn.some((key) => newHeaders[key] !== (invoiceColumns?.[key] ?? null));
      setHasChanges(changed);

      return newHeaders;
    });

    setDropdownOpen({ ...dropdownOpen, [col]: false });
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setHasChanges(false);
  };

  const handleUpdate = () => {
    handleSubmit();
    setIsEditMode(false);
    setHasChanges(false);
  };

  const handleFileUpload = (event) => {
    let file;

    // If called from react-dropzone (array of files)
    if (Array.isArray(event)) {
      file = event[0];
    }
    // If called from <input type="file" />
    else if (event?.target?.files) {
      file = event.target.files[0];
    }

    if (!file) return;

    setMappingFile(file);

    setHeaders([]);
    // setSelectedHeaders({});

    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });

      const normalizedSheetNames = workbook.SheetNames.map((name) => name.trim().toLowerCase());

      const detailIndex = normalizedSheetNames.findIndex((name) => name === "detail" || name === "details");

      const sheetName = detailIndex !== -1 ? workbook.SheetNames[detailIndex] : workbook.SheetNames[0];

      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length === 0) return;

      let headerRowIndex = 0;

      const firstRow = jsonData[0];
      const isFirstRowHeader = firstRow.every((cell) => typeof cell === "string" && cell.trim() !== "");

      if (isFirstRowHeader) {
        headerRowIndex = 0;
      } else {
        const possibleHeaderRow = jsonData.findIndex(
          (row) => row.filter((cell) => typeof cell === "string" && cell.trim() !== "").length >= row.length / 2
        );
        headerRowIndex = possibleHeaderRow !== -1 ? possibleHeaderRow : 0;
      }

      let headers = jsonData[headerRowIndex];

      if (headers.length === 1 && typeof headers[0] === "string") {
        const possibleSplit = headers[0].split(/[\|,;]+/).map((h) => h.trim());
        if (possibleSplit.length > 1) {
          headers = possibleSplit;
        }
      }

      if (headers.length === 1 && jsonData.length > 1) {
        const verticalHeaders = jsonData.map((row) => row[0]).filter((val) => typeof val === "string" && val.trim() !== "");
        if (verticalHeaders.length > 1) {
          headers = verticalHeaders;
        }
      }

      setHeaders(headers);
    };

    reader.readAsArrayBuffer(file);
  };

  function handleSubmit() {
    if (mappingFile === null) {
      toast.warning("Please upload the excel file first.");
      return;
    }

    const completeData = mainTableColumn.reduce((acc, col) => {
      const value = selectedHeaders[col];
      if (value !== null && value !== undefined && value !== "") {
        acc[col] = value;
      }
      return acc;
    }, {});

    if (Object.keys(completeData).length === 0) {
      toast.warning("Please assign at least one Excel header before submitting.");
      return;
    }

    if (manualPayout) {
      if (!payoutDate) {
        toast.warning("Please select payout date manually.");
        return;
      }

      completeData["payout_date"] = moment(payoutDate).format("YYYY-MM-DD");
    }

    const payload = {
      id : partner ? partner?.id : null,
      data: completeData,
      isHavingColumns: false,
    };

    setLoading(true);
    dispatch(addPartnerInvoiceData(payload)).then((res) => {
      setLoading(false);
      if (!res.error) {
        setMappingFile(null);
        setIsHavingColumns(res.payload.is_having_invoice_columns ?? false);
        setfields(res.payload.partner.fields ?? {});
      }
    });
  }

  return (
    <Fragment>
      <div className="upload-invoice">
        {!isHavingColumns && (
          <Row>
            <Col md={12}>
              <Alert color="warning" className="my-1">
                <p style={{ color: "#664d03", fontSize: 14, fontWeight: 600 }} className="mb-1">
                  You haven’t defined your columns yet. Please upload your Excel sheet first and map your headers to the database columns on the left.
                </p>
                <p style={{ color: "#664d03", marginTop: 0 }}>
                  <strong>Note:</strong> Please make sure you map your headers to our database columns correctly. If your Excel file doesn’t have
                  certain columns shown on the left, leave them empty. Once you submit, this mapping cannot be changed in the future.
                </p>
              </Alert>
            </Col>
          </Row>
        )}

        {isEditMode && (
          <Row>
            <Col md={12}>
              <Alert color="warning" className="my-1">
                <p style={{ color: "#664d03", fontSize: 14, fontWeight: 600 }} className="mb-1">
                  Please upload your Excel sheet again to update the mapping.
                </p>
                <p style={{ color: "#664d03", marginTop: 0 }}>
                  <strong>Note:</strong> Please make sure you map your headers to our database columns correctly. If your Excel file doesn’t have
                  certain columns shown on the left, leave them empty.
                </p>
              </Alert>
            </Col>
          </Row>
        )}

        <Row className="mb-3">
          {(!isHavingColumns || isEditMode) && (
            <Col sm={12}>
              <DropZone
                placeholder="Upload your excel file here"
                extension=".csv,.xls,.xlsx"
                onChangeFile={handleFileUpload}
                customContainerStles={{ paddingRight: 0, paddingLeft: 0 }}
                isInvoiceUpload
              />
            </Col>
          )}

          {mappingFile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: "10px",
                backgroundColor: "#f8f9fa",
                padding: "10px 15px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                marginLeft: 17,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "16px" }}>📄</span>
                <span style={{ color: "#333", fontWeight: 500 }}>{mappingFile.name}</span>
              </div>
              <button
                onClick={() => setMappingFile(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#dc3545",
                  fontSize: "18px",
                }}
                title="Remove file"
              >
                &times;
              </button>
            </div>
          )}
        </Row>

        {(mappingFile || isHavingColumns) && (
          <Table bordered style={{ overflowX: "hidden" }}>
            <thead>
              <tr>
                <th>Database Column</th>
                <th>Excel Header</th>
              </tr>
            </thead>
            <tbody>
              {mainTableColumn.map((col, idx) => (
                <tr key={idx}>
                  <td>
                    <span style={{ color: "white", background: "#3E4A56" }} className="btn">
                      {col}
                    </span>
                  </td>
                  <td>
                    {col === "payout_date" ? (
                      <Row>
                        <Col sm={6}>
                          <Dropdown isOpen={dropdownOpen[col]} toggle={() => toggleDropdown(col)}>
                            <DropdownToggle
                              caret
                              color="transparent"
                              disabled={(isHavingColumns && (!isEditMode || !mappingFile)) || manualPayout}
                              className="border border-gray-300 bg-light text-dark shadow-none px-3 py-1 rounded"
                            >
                              {manualPayout ? "Disabled" : selectedHeaders[col] || "Select Excel Header"}
                            </DropdownToggle>

                            <DropdownMenu container="body" className="invoice-dropdown-menu">
                              {headers.map((header, hIdx) => (
                                <DropdownItem key={hIdx} onClick={() => handleSelect(col, header)}>
                                  {header}
                                </DropdownItem>
                              ))}
                            </DropdownMenu>
                          </Dropdown>
                        </Col>

                        <Col sm={3}>
                          <div className="form-check form-switch mt-2 d-flex align-items-center">
                            <input
                              type="checkbox"
                              className="form-check-input mt-0"
                              checked={manualPayout}
                              disabled={isHavingColumns && (!isEditMode || !mappingFile)}
                              onChange={(e) => {
                                setManualPayout(e.target.checked);
                                if (e.target.checked) {
                                  handleSelect(col, null);
                                }
                              }}
                            />
                            <label className="form-check-label">Select manually</label>
                          </div>
                        </Col>

                        <Col sm={3}>
                          {manualPayout && (
                            <DatePicker
                              format="yyyy-MM-dd"
                              className="custom-date-picker"
                              placeholder="Select Payout Date"
                              showMeridian={false}
                              disabled={isHavingColumns && (!isEditMode || !mappingFile)}
                              style={{ width: "100%" }}
                              onChange={(date) => {
                                setPayoutDate(date);
                                setHasChanges(true);
                              }}
                              value={payoutDate}
                            />
                          )}
                        </Col>
                      </Row>
                    ) : (
                      <Row>
                        <Col sm={6}>
                          <Dropdown isOpen={dropdownOpen[col]} toggle={() => toggleDropdown(col)}>
                            <DropdownToggle
                              caret
                              color="transparent"
                              disabled={isHavingColumns && (!isEditMode || !mappingFile)}
                              className={`border border-gray-300 bg-light text-dark shadow-none px-3 py-1 rounded ${
                                isHavingColumns ? "opacity-75 cursor-not-allowed" : ""
                              }`}
                              style={{
                                backgroundColor: "#f5f5f5",
                                borderColor: "#dcdcdc",
                              }}
                            >
                              {selectedHeaders[col] || (isHavingColumns ? "-" : "Select Excel Header")}
                            </DropdownToggle>
                            <DropdownMenu
                              container="body"
                              className="invoice-dropdown-menu"
                              style={{
                                maxHeight: "200px",
                                overflowY: "auto",
                                minWidth: "200px",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                zIndex: 1050,
                              }}
                            >
                              {headers.map((header, hIdx) => (
                                <DropdownItem key={hIdx} onClick={() => handleSelect(col, header)} style={{ borderBottom: "none" }}>
                                  {header}
                                </DropdownItem>
                              ))}
                            </DropdownMenu>
                          </Dropdown>
                        </Col>

                        <Col sm={3}>
                          {selectedHeaders[col] && ((!isHavingColumns && mappingFile) || (isEditMode && mappingFile)) ? (
                            <button
                              type="button"
                              onClick={() => handleSelect(col, null)}
                              className="btn btn-outline-danger btn-sm"
                              title="Clear this mapping"
                            >
                              Clear
                            </button>
                          ) : (
                            <span style={{ color: "#aaa", fontSize: "13px" }}></span>
                          )}
                        </Col>
                      </Row>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <div className="mt-2 d-flex justify-content-end">
          <div className="p-2 d-flex" style={{ gap: 8 }}>
            {!isHavingColumns && (
              <Button
                color="primary"
                disabled={loading || !partner || Object.keys(partner).length === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSubmit();
                }}
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin fa-lg spinner" /> Submitting
                  </>
                ) : (
                  "Submit Mapping Headers"
                )}
              </Button>
            )}

            {isHavingColumns && (
              <>
                {isEditMode && (
                  <Button
                    color="primary"
                    disabled={loading}
                    onClick={() => {
                      setIsEditMode(false);
                      setHasChanges(false);
                      setMappingFile(null);
                      setSelectedHeaders(() =>
                        mainTableColumn.reduce((acc, col) => {
                          acc[col] = invoiceColumns && typeof invoiceColumns === "object" ? invoiceColumns[col] ?? null : null;
                          return acc;
                        }, {})
                      );
                    }}
                  >
                    Cancel
                  </Button>
                )}
                <Button color="danger" outline disabled={loading} onClick={handleEdit}>
                  Edit
                </Button>

                <Button
                  color="primary"
                  disabled={!hasChanges || !mappingFile || loading}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpdate();
                  }}
                >
                  {loading ? (
                    <>
                      <i className="fa fa-spinner fa-spin fa-lg spinner" /> Updating
                    </>
                  ) : (
                    "Update"
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default UploadInvoices;
